import { useState, ChangeEvent, FormEvent, JSX, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRegister } from "../../hooks/useAuth";
import styles from "./Register.module.css";
import logo from "../../assets/logo.png";

interface FormData {
  fullName:        string;
  agencyName:      string;
  email:           string;
  password:        string;
  confirmPassword: string;
}

interface FormErrors {
  fullName?:        string;
  agencyName?:      string;
  email?:           string;
  password?:        string;
  confirmPassword?: string;
  general?:         string;
}

const PERKS = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10"/>
        <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
        <line x1="9" y1="9" x2="9.01" y2="9"/>
        <line x1="15" y1="9" x2="15.01" y2="9"/>
      </svg>
    ),
    label: "Flight & Hotel Booking Engine",
    desc: "Real-time inventory access"
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
    label: "Custom Domain + Free SSL",
    desc: "Professional brand identity"
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
    label: "Go live in under 10 minutes",
    desc: "Zero configuration required"
  },
];

const TRUST_ITEMS = [
  { icon: "🔐", label: "Bank-grade encryption" },
  { icon: "✓", label: "GDPR compliant" },
  { icon: "🛡️", label: "SOC 2 certified" },
];

function getStrength(pw: string) {
  if (!pw) return { score: 0, label: "", color: "" };
  let s = 0;
  if (pw.length >= 8)           s++;
  if (/[A-Z]/.test(pw))         s++;
  if (/[0-9]/.test(pw))         s++;
  if (/[^A-Za-z0-9]/.test(pw))  s++;
  const map = [
    { label: "Too short", color: "var(--error)" },
    { label: "Weak",      color: "var(--warning)" },
    { label: "Fair",      color: "var(--warning-light)" },
    { label: "Good",      color: "var(--success)" },
    { label: "Excellent", color: "var(--success-dark)" },
  ];
  return { score: s, ...map[s] };
}

function InputIcon({ type }: { type: string }): JSX.Element {
  const iconProps = { 
    width: "16", 
    height: "16", 
    viewBox: "0 0 24 24", 
    fill: "none", 
    stroke: "currentColor", 
    strokeWidth: "2", 
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const
  };
  
  switch (type) {
    case "name": return (
      <svg {...iconProps}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    );
    case "building": return (
      <svg {...iconProps}>
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
        <path d="M9 22v-4h6v4"/>
        <path d="M8 6h.01"/>
        <path d="M16 6h.01"/>
        <path d="M12 6h.01"/>
        <path d="M12 10h.01"/>
        <path d="M12 14h.01"/>
        <path d="M16 10h.01"/>
        <path d="M16 14h.01"/>
        <path d="M8 10h.01"/>
        <path d="M8 14h.01"/>
      </svg>
    );
    case "mail": return (
      <svg {...iconProps}>
        <rect x="2" y="4" width="20" height="16" rx="2"/>
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
      </svg>
    );
    default: return (
      <svg {...iconProps}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    );
  }
}

function EyeToggle({ visible, onClick }: { visible: boolean; onClick: () => void }): JSX.Element {
  const svgProps = {
    width: "16",
    height: "16", 
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const
  };

  return (
    <button type="button" onClick={onClick} className={styles.eyeBtn} tabIndex={-1} aria-label={visible ? "Hide password" : "Show password"}>
      {visible ? (
        <svg {...svgProps}>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
      ) : (
        <svg {...svgProps}>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      )}
    </button>
  );
}

const Register = (): JSX.Element => {
  const navigate = useNavigate();
  const { execute: registerUser, isLoading, error: apiError } = useRegister();

  const [form,    setForm]    = useState<FormData>({ fullName: "", agencyName: "", email: "", password: "", confirmPassword: "" });
  const [errors,  setErrors]  = useState<FormErrors>({});
  const [showPw,  setShowPw]  = useState(false);
  const [showCPw, setShowCPw] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (apiError) setErrors((p) => ({ ...p, general: apiError }));
  }, [apiError]);

  const strength = getStrength(form.password);
  const filled   = (v: string) => v.trim().length > 0;
  const pwMatch  = form.password && form.confirmPassword && form.password === form.confirmPassword;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name as keyof FormErrors]) setErrors(p => ({ ...p, [name]: undefined }));
  };

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!form.fullName.trim())         e.fullName        = "Full name is required";
    if (!form.agencyName.trim())       e.agencyName      = "Agency name is required";
    if (!form.email.trim())            e.email           = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.password)                e.password        = "Password is required";
    else if (form.password.length < 8) e.password        = "Minimum 8 characters";
    if (!form.confirmPassword)         e.confirmPassword = "Please confirm your password";
    else if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords don't match";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const result = await registerUser(form.fullName.trim(), form.email.trim().toLowerCase(), form.password, form.agencyName.trim(), form.confirmPassword);
    if (result) {
      navigate("/plans", {
        state: {
          email: form.email.trim().toLowerCase(),
          agencyName: form.agencyName.trim()
        }
      });
    }
  };

  const inputClass = (name: string, extra = "") =>
    [styles.inputWrap,
     focused === name ? styles.inputFocus : "",
     errors[name as keyof FormErrors] ? styles.inputError : "",
     filled(form[name as keyof FormData]) && !errors[name as keyof FormErrors] ? styles.inputSuccess : "",
     extra].join(" ");

  return (
    <div className={`${styles.page} ${mounted ? styles.mounted : ''}`}>

      {/* LEFT BRAND PANEL */}
      <aside className={styles.panel}>
        <div className={styles.panelBg}>
          <div className={styles.panelNoise} />
          <div className={styles.gradient1} />
          <div className={styles.gradient2} />
          <div className={styles.gradient3} />
          <div className={styles.gridPattern} />
        </div>

        <div className={styles.panelContent}>
          
          <div className={styles.logoSection}>
            <img src={logo} alt="FlyGDS" className={styles.logo} />
            <div className={styles.betaBadge}>
              <span className={styles.betaDot} />
              Enterprise Platform
            </div>
          </div>

          <div className={styles.hero}>
            <h1 className={styles.heroTitle}>
              Launch your travel platform in <span className={styles.accent}>minutes</span>, not months
            </h1>
            <p className={styles.heroDesc}>
              Enterprise-grade booking engine with your branding. Connect your GDS and start accepting bookings instantly.
            </p>
          </div>

          <div className={styles.features}>
            {PERKS.map((perk, i) => (
              <div 
                key={i} 
                className={styles.feature}
                style={{ animationDelay: `${0.2 + i * 0.1}s` }}
              >
                <div className={styles.featureIcon}>{perk.icon}</div>
                <div className={styles.featureContent}>
                  <div className={styles.featureLabel}>{perk.label}</div>
                  <div className={styles.featureDesc}>{perk.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* <div className={styles.stats}>
            <div className={styles.stat}>
              <div className={styles.statValue}>500+</div>
              <div className={styles.statLabel}>Active Agencies</div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <div className={styles.statValue}>50K+</div>
              <div className={styles.statLabel}>Monthly Bookings</div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <div className={styles.statValue}>99.9%</div>
              <div className={styles.statLabel}>Uptime SLA</div>
            </div>
          </div> */}

        </div>
      </aside>

      {/* RIGHT FORM PANEL */}
      <main className={styles.formSide}>
        
        <div className={styles.formContainer}>
          
          {/* Progress Stepper */}
          <div className={styles.stepper}>
            {["Account", "Plan", "Payment", "Launch"].map((step, i) => (
              <div key={i} className={styles.step}>
                <div className={`${styles.stepCircle} ${i === 0 ? styles.stepActive : ''}`}>
                  {i === 0 ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <span>{i + 1}</span>
                  )}
                </div>
                <span className={`${styles.stepLabel} ${i === 0 ? styles.stepLabelActive : ''}`}>
                  {step}
                </span>
              </div>
            ))}
          </div>

          <div className={styles.formCard}>
            
            <div className={styles.header}>
              <h2 className={styles.title}>Create your account</h2>
              <p className={styles.subtitle}>
                Get started in 60 seconds • No credit card required
              </p>
            </div>

            {errors.general && (
              <div className={styles.alert}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{errors.general}</span>
              </div>
            )}

            <form onSubmit={onSubmit} noValidate className={styles.form}>

              <div className={styles.gridRow}>
                <div className={styles.field}>
                  <label htmlFor="fullName" className={styles.label}>Full Name</label>
                  <div className={inputClass("fullName")}>
                    <span className={styles.inputIcon}>
                      <InputIcon type="name" />
                    </span>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      value={form.fullName}
                      onChange={onChange}
                      onFocus={() => setFocused("fullName")}
                      onBlur={() => setFocused(null)}
                      placeholder="John Smith"
                      className={styles.input}
                      autoComplete="name"
                    />
                    {filled(form.fullName) && !errors.fullName && <CheckIcon />}
                  </div>
                  {errors.fullName && <ErrorMsg msg={errors.fullName} />}
                </div>

                <div className={styles.field}>
                  <label htmlFor="agencyName" className={styles.label}>Agency Name</label>
                  <div className={inputClass("agencyName")}>
                    <span className={styles.inputIcon}>
                      <InputIcon type="building" />
                    </span>
                    <input
                      id="agencyName"
                      name="agencyName"
                      type="text"
                      value={form.agencyName}
                      onChange={onChange}
                      onFocus={() => setFocused("agencyName")}
                      onBlur={() => setFocused(null)}
                      placeholder="Acme Travel Ltd."
                      className={styles.input}
                      autoComplete="organization"
                    />
                    {filled(form.agencyName) && !errors.agencyName && <CheckIcon />}
                  </div>
                  {errors.agencyName && <ErrorMsg msg={errors.agencyName} />}
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>Email Address</label>
                <div className={inputClass("email")}>
                  <span className={styles.inputIcon}>
                    <InputIcon type="mail" />
                  </span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    onFocus={() => setFocused("email")}
                    onBlur={() => setFocused(null)}
                    placeholder="john@acmetravel.com"
                    className={styles.input}
                    autoComplete="email"
                  />
                  {filled(form.email) && !errors.email && <CheckIcon />}
                </div>
                {errors.email && <ErrorMsg msg={errors.email} />}
              </div>

              <div className={styles.gridRow}>
                <div className={styles.field}>
                  <label htmlFor="password" className={styles.label}>Password</label>
                  <div className={inputClass("password")}>
                    <span className={styles.inputIcon}>
                      <InputIcon type="lock" />
                    </span>
                    <input
                      id="password"
                      name="password"
                      type={showPw ? "text" : "password"}
                      value={form.password}
                      onChange={onChange}
                      onFocus={() => setFocused("password")}
                      onBlur={() => setFocused(null)}
                      placeholder="Min. 8 characters"
                      className={styles.input}
                      autoComplete="new-password"
                    />
                    <EyeToggle visible={showPw} onClick={() => setShowPw(v => !v)} />
                  </div>
                  {form.password && (
                    <div className={styles.strength}>
                      <div className={styles.strengthBars}>
                        {[0, 1, 2, 3].map(i => (
                          <div
                            key={i}
                            className={`${styles.strengthBar} ${i < strength.score ? styles.strengthBarActive : ''}`}
                            style={{ 
                              background: i < strength.score ? strength.color : 'var(--gray-200)'
                            }}
                          />
                        ))}
                      </div>
                      <span className={styles.strengthLabel} style={{ color: strength.color }}>
                        {strength.label}
                      </span>
                    </div>
                  )}
                  {errors.password && <ErrorMsg msg={errors.password} />}
                </div>

                <div className={styles.field}>
                  <label htmlFor="confirmPassword" className={styles.label}>Confirm Password</label>
                  <div className={inputClass("confirmPassword")}>
                    <span className={styles.inputIcon}>
                      <InputIcon type="lock" />
                    </span>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showCPw ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={onChange}
                      onFocus={() => setFocused("confirmPassword")}
                      onBlur={() => setFocused(null)}
                      placeholder="Re-enter password"
                      className={styles.input}
                      autoComplete="new-password"
                    />
                    <EyeToggle visible={showCPw} onClick={() => setShowCPw(v => !v)} />
                    {pwMatch && <CheckIcon />}
                  </div>
                  {errors.confirmPassword && <ErrorMsg msg={errors.confirmPassword} />}
                </div>
              </div>

              <p className={styles.terms}>
                By continuing, you agree to our{" "}
                <a href="/terms" className={styles.link}>Terms of Service</a> and{" "}
                <a href="/privacy" className={styles.link}>Privacy Policy</a>
              </p>

              <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className={styles.spinner} />
                    Creating your account...
                  </>
                ) : (
                  <>
                    Create Account & Choose Plan
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </>
                )}
              </button>

            </form>

            <div className={styles.divider}>
              <span>Already have an account?</span>
            </div>

            <a href="/login" className={styles.loginLink}>
              Sign in to your account
            </a>

            {/* <div className={styles.trustBar}>
              {TRUST_ITEMS.map((item, i) => (
                <div key={i} className={styles.trustItem}>
                  <span className={styles.trustIcon}>{item.icon}</span>
                  <span className={styles.trustLabel}>{item.label}</span>
                </div>
              ))}
            </div> */}

          </div>
        </div>

      </main>

    </div>
  );
};

function CheckIcon(): JSX.Element {
  return (
    <span className={styles.checkIcon}>
      <svg 
        width="13" 
        height="13" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="3" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    </span>
  );
}

function ErrorMsg({ msg }: { msg: string }): JSX.Element {
  return (
    <p className={styles.error}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12" stroke="white" strokeWidth="2"/>
        <line x1="12" y1="16" x2="12.01" y2="16" stroke="white" strokeWidth="2"/>
      </svg>
      {msg}
    </p>
  );
}
export default Register;